import { GameLoader } from '@/components/projects/games/game-loader';
import { ErrorBoundary } from '@/components/ui';
import { useIDESelectors } from '@/hooks/use-ide-selector';
import { useIDEState } from '@/hooks/use-ide-state';
import React, { memo, Suspense, useEffect } from 'react';

export const FileViewer: React.FC = memo(() => {
    const activeTabData = useIDESelectors.useActiveTabData();
    const { closeTab } = useIDEState();
    const { activeTab, file } = activeTabData || { activeTab: null, file: null };

    // Close the active tab if the file is not found and switch to next available tab
    useEffect(() => {
        if (activeTab && !file) {
            console.warn('File not found for current active tab, closing tab:', activeTab);
            closeTab(activeTab.id);
        }
    }, [activeTab, file, closeTab]);

    if (!activeTab) {
        return (
            <div
                id='file-viewer-empty'
                className='h-full bg-vscode-bg flex items-center justify-center overflow-y-auto'
            >
                <div className='text-center'>
                    <div className='text-vscode-text-muted text-sm mb-2'>There are no files open</div>
                    <div className='text-vscode-text-muted text-xs'>
                        Select a file via the file explorer on the left
                    </div>
                </div>
            </div>
        );
    }

    if (!file) {
        return (
            <div
                id='file-viewer-not-found'
                className='h-full bg-vscode-bg flex items-center justify-center overflow-y-auto'
            >
                <div className='text-center'>
                    <div className='text-vscode-text-muted text-sm'>File not found</div>
                    <div className='text-vscode-text-muted text-xs'>Closing tab...</div>
                </div>
            </div>
        );
    }

    // Render based on file type
    if (file.type === 'iframe') {
        return (
            <div id='file-viewer-iframe' className='h-full bg-vscode-bg overflow-y-auto'>
                <iframe
                    src={file.content}
                    className='w-full h-full border-0'
                    title={file.name}
                    sandbox={
                        file.sandbox === false
                            ? undefined
                            : file.sandbox !== true && file.sandbox !== undefined
                              ? file.sandbox
                              : 'allow-same-origin allow-scripts'
                    }
                />
            </div>
        );
    } else if (file.type === 'component') {
        const Component = file.content as React.ComponentType;
        return (
            <div id='file-viewer-component' className='h-full bg-vscode-bg overflow-y-auto'>
                <ErrorBoundary>
                    <Suspense
                        fallback={
                            <div className='h-full flex items-center justify-center'>
                                <div className='text-center'>
                                    <div className='text-vscode-text text-2xl mb-2'>{file.name}</div>
                                    <div className='text-vscode-text-muted text-xs'>Loading...</div>
                                </div>
                            </div>
                        }
                    >
                        <Component />
                    </Suspense>
                </ErrorBoundary>
            </div>
        );
    } else if (file.type === 'game') {
        const gameName = file.game;
        return (
            <div id='file-viewer-game' className='h-full bg-vscode-bg overflow-y-auto'>
                <ErrorBoundary>
                    <GameLoader gameName={gameName} />
                </ErrorBoundary>
            </div>
        );
    }

    // This should never happen with our discriminated union, but TypeScript needs it
    console.warn('Unknown file type', file);
    return (
        <div id='file-viewer-unknown' className='h-full bg-vscode-bg p-4 overflow-y-auto'>
            <div className='text-vscode-text'>
                <h2 className='text-lg font-medium mb-4'>Unknown File</h2>
                <div className='text-vscode-text-muted'>Unknown file type</div>
            </div>
        </div>
    );
});
